import * as React from 'react';
import { RefreshCw, ExternalLink, Wifi, WifiOff, AlertCircle, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { cn } from '@/lib/utils';
import { formatCurrency as fmtCurrency, formatNumber } from '@/lib/number-format';

export interface EnhancedExchangeConnection {
  id: string;
  name: string;
  status: 'connected' | 'disconnected' | 'error';
  balance: number;
  currency: string;
  activeBots?: number;
  last24hVolume?: number;
  latencyMs?: number;
  lastSync?: string;
  percentOfTotal?: number;
}

export interface EnhancedWalletConnection {
  id: string;
  name: string;
  status: 'connected' | 'disconnected' | 'error';
  network: string;
  balance: number;
  currency: string;
  balanceLabel?: string;
  balanceHint?: string;
  latencyMs?: number;
}

export interface EnhancedExchangeSummaryProps {
  exchanges: EnhancedExchangeConnection[];
  wallets?: EnhancedWalletConnection[];
  onAddExchange?: () => void;
  onSyncExchange?: (id: string) => void;
  onViewExchange?: (id: string) => void;
  addButtonLabel?: string;
  locale?: string;
  showPieChart?: boolean;
  className?: string;
}

// Rugira brand palette (red-first + neutral grays) for connection distribution.
const PIE_COLORS = ['#E10500', '#B30000', '#FF5454', '#9CA3AF', '#6B7280', '#374151', '#D1D5DB', '#E5E7EB'];

function getStatusIcon(status: EnhancedExchangeConnection['status']) {
  switch (status) {
    case 'connected':
      return <Wifi className="h-3.5 w-3.5 text-emerald-500" />;
    case 'error':
      return <AlertCircle className="h-3.5 w-3.5 text-rose-500" />;
    default:
      return <WifiOff className="h-3.5 w-3.5 text-slate-400" />;
  }
}

function getLatencyColor(latencyMs?: number) {
  if (latencyMs === undefined) return 'text-slate-400';
  if (latencyMs < 50) return 'text-emerald-500';
  if (latencyMs < 150) return 'text-amber-500';
  return 'text-rose-500';
}

function formatLatency(latencyMs?: number) {
  if (latencyMs === undefined) return '—';
  return `${latencyMs}ms`;
}

function formatNetworkLabel(network: string) {
  const cleaned = network.trim();
  if (!cleaned) return 'Unknown network';
  return cleaned
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => (token.length <= 4 ? token.toUpperCase() : `${token[0].toUpperCase()}${token.slice(1).toLowerCase()}`))
    .join(' ');
}

function MiniPieChart({
  data,
  size = 120,
  className,
}: {
  data: { label: string; value: number; color: string }[];
  size?: number;
  className?: string;
}) {
  const total = data.reduce((acc, d) => acc + d.value, 0);
  if (total === 0) return null;

  const radius = size / 2 - 10;
  const centerX = size / 2;
  const centerY = size / 2;

  let cumulativePercent = 0;

  const paths = data.map((d, i) => {
    const percent = d.value / total;
    const startAngle = cumulativePercent * 2 * Math.PI - Math.PI / 2;
    cumulativePercent += percent;
    const endAngle = cumulativePercent * 2 * Math.PI - Math.PI / 2;

    const x1 = centerX + radius * Math.cos(startAngle);
    const y1 = centerY + radius * Math.sin(startAngle);
    const x2 = centerX + radius * Math.cos(endAngle);
    const y2 = centerY + radius * Math.sin(endAngle);

    const largeArcFlag = percent > 0.5 ? 1 : 0;

    const pathD = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    return (
      <path
        key={d.label}
        d={pathD}
        fill={d.color}
        stroke="white"
        strokeWidth="2"
        className="transition-opacity hover:opacity-80"
      />
    );
  });

  return (
    <svg width={size} height={size} className={className} role="img" aria-label="Portfolio distribution pie chart">
      {paths}
    </svg>
  );
}

export function EnhancedExchangeSummary({
  exchanges,
  wallets = [],
  onAddExchange,
  onSyncExchange,
  onViewExchange,
  addButtonLabel = 'Manage',
  locale = 'de-CH',
  showPieChart = true,
  className,
}: EnhancedExchangeSummaryProps) {
  const formatCurrency = React.useCallback(
    (value: number) =>
      fmtCurrency(value, { locale, decimals: 0, sign: 'never' }),
    [locale]
  );

  const totalBalance = React.useMemo(
    () =>
      exchanges.reduce((acc, e) => acc + e.balance, 0) +
      wallets.reduce((acc, wallet) => acc + wallet.balance, 0),
    [exchanges, wallets]
  );

  const connectedExchangeCount = React.useMemo(
    () => exchanges.filter((e) => e.status === 'connected').length,
    [exchanges]
  );

  const connectedWalletCount = React.useMemo(
    () => wallets.filter((wallet) => wallet.status === 'connected').length,
    [wallets]
  );

  const totalBots = React.useMemo(
    () => exchanges.reduce((acc, e) => acc + (e.activeBots ?? 0), 0),
    [exchanges]
  );

  const pieData = React.useMemo(() => {
    return exchanges
      .filter((e) => e.balance > 0)
      .map((e, i) => ({
        label: e.name,
        value: e.balance,
        color: PIE_COLORS[i % PIE_COLORS.length],
      }));
  }, [exchanges]);

  const exchangesWithPercent = React.useMemo(() => {
    return exchanges.map((e, i) => ({
      ...e,
      percentOfTotal: totalBalance > 0 ? (e.balance / totalBalance) * 100 : 0,
      color: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [exchanges, totalBalance]);

  if (exchanges.length === 0 && wallets.length === 0) {
    return (
      <Card className={cn('border-2 border-dashed border-slate-200 bg-white', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-700">Connections</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 text-sm text-slate-500">
          <p>
            Link your exchange and wallet connections to monitor uptime, balances, and bot coverage directly from the overview.
          </p>
          {onAddExchange && (
            <div>
              <Button type="button" size="sm" onClick={onAddExchange}>
                Go to Connections
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const summaryParts = [
    `${connectedExchangeCount}/${exchanges.length} exchanges`,
    `${connectedWalletCount}/${wallets.length} wallets`,
    `${formatCurrency(totalBalance)} total`,
    `${totalBots} active bots`,
  ];

  return (
    <Card className={cn('border border-slate-200', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold">Connections</CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              {summaryParts.join(' · ')}
            </p>
          </div>
          {onAddExchange && (
            <Button variant="outline" size="sm" onClick={onAddExchange} className="shrink-0">
              {addButtonLabel}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {exchanges.length > 0 && (
            <div className={cn('flex gap-6', showPieChart && pieData.length > 1 ? 'flex-col lg:flex-row' : 'flex-col')}>
              {showPieChart && pieData.length > 1 && (
                <div className="flex flex-col items-center justify-center lg:w-40">
                  <MiniPieChart data={pieData} size={120} />
                  <p className="mt-2 text-xs font-medium text-slate-500">Distribution</p>
                </div>
              )}

              <div className="flex-1">
                <div className="grid gap-3 sm:grid-cols-2">
                  {exchangesWithPercent.map((exchange) => (
                    <div
                      key={exchange.id}
                      className="group relative rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition-all hover:border-slate-300 hover:shadow"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="h-3 w-3 rounded-full shrink-0"
                            style={{ backgroundColor: exchange.color }}
                            aria-hidden="true"
                          />
                          <span className="font-medium text-slate-900 text-sm">{exchange.name}</span>
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>{getStatusIcon(exchange.status)}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {exchange.status === 'connected'
                                ? 'Connected'
                                : exchange.status === 'error'
                                  ? 'Connection error'
                                  : 'Disconnected'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      <div className="mt-2 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-semibold text-slate-900 tabular-nums">
                            {formatCurrency(exchange.balance)}
                          </span>
                          <span className="text-xs text-slate-500 tabular-nums">
                            {formatNumber(exchange.percentOfTotal, { decimals: 0, sign: 'never' })}%
                          </span>
                        </div>

                        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${exchange.percentOfTotal ?? 0}%`,
                              backgroundColor: exchange.color,
                            }}
                          />
                        </div>

                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span className={getLatencyColor(exchange.latencyMs)}>
                            {formatLatency(exchange.latencyMs)}
                          </span>
                          {exchange.activeBots !== undefined && (
                            <span>{exchange.activeBots} bots</span>
                          )}
                        </div>
                      </div>

                      <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        {onSyncExchange && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => onSyncExchange(exchange.id)}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
                                  aria-label={`Sync ${exchange.name}`}
                                >
                                  <RefreshCw className="h-3 w-3" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Sync now</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {onViewExchange && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => onViewExchange(exchange.id)}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
                                  aria-label={`View ${exchange.name} details`}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>View details</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className={cn('space-y-3', exchanges.length > 0 && 'border-t border-slate-100 pt-5')}>
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-slate-800">Wallet Connections</h4>
              <span className="text-xs text-slate-500">{connectedWalletCount}/{wallets.length} connected</span>
            </div>
            {wallets.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {wallets.map((wallet) => (
                  <div
                    key={wallet.id}
                    className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 space-y-1">
                        <p className="truncate text-sm font-medium text-slate-900">{wallet.name}</p>
                        <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                          <Wallet className="h-3 w-3" />
                          {formatNetworkLabel(wallet.network)}
                        </div>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>{getStatusIcon(wallet.status)}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {wallet.status === 'connected'
                              ? 'Connected'
                              : wallet.status === 'error'
                                ? 'Connection error'
                                : 'Disconnected'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="text-base font-semibold text-slate-900 tabular-nums">
                        {wallet.balanceLabel ??
                          fmtCurrency(wallet.balance, {
                            locale,
                            currency: wallet.currency || 'CHF',
                            decimals: 0,
                            sign: 'never',
                          })}
                      </span>
                      <span className={cn('tabular-nums', getLatencyColor(wallet.latencyMs))}>
                        {formatLatency(wallet.latencyMs)}
                      </span>
                    </div>
                    {wallet.balanceHint ? (
                      <p className="mt-1 text-[11px] text-slate-500">{wallet.balanceHint}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
                No wallet connections yet. Add watch-only wallets from the Connections page to track networks and balances.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
